import styled from '@emotion/styled';
import { FC, HTMLProps, useCallback, useState } from 'react';

import Flex from './Flex';

// Local Typings
type ImageSize = 'full' | 'large' | 'medium' | 'micro' | 'small' | 'thumbnail';
interface Props {
  alt: string;
  height?: number | string;
  imageSize?: ImageSize;
  loadingImageSrc?: string;
  maxWidth?: number | string;
  src: string;
}
interface StyledImageProps extends Props {
  filter: string;
}

// Local Variables
const imageSizeValues = {
  large: 160,
  medium: 130,
  micro: 25,
  small: 100,
  thumbnail: 75,
};
const imageSizes: Record<ImageSize, string> = {
  full: '100%',
  large: `${imageSizeValues.large}px`,
  medium: `${imageSizeValues.medium}px`,
  micro: `${imageSizeValues.micro}px`,
  small: `${imageSizeValues.small}px`,
  thumbnail: `${imageSizeValues.thumbnail}px`,
};
function getHeight({ height = 'auto', imageSize = 'full' }: Props) {
  if (imageSize === 'thumbnail') {
    return imageSizes.thumbnail;
  }

  return height;
}
function getWidth({ imageSize }: Props) {
  return imageSize ? imageSizes[imageSize] : 'auto';
}
function getFilter(props: Props,
  isLoaded: boolean) {
  if (props.loadingImageSrc) {
    return isLoaded ? 'none' : 'blur(5px)';
  }

  return 'none';
}
const StyledSpan = styled.span({
  overflow: 'hidden',
  position: 'relative',
});
const StyledImage = styled.img<StyledImageProps>(props => ({
  display: 'block',
  filter: props.filter,
  height: getHeight(props),
  maxWidth: props.maxWidth,
  objectFit: 'contain',
  width: getWidth(props),
}));
const AbsoluteImage = styled.img<Props>(props => ({
  bottom: 0,
  display: 'block',
  filter: 'blur(5px)',
  height: getHeight(props),
  left: 0,
  maxWidth: props.maxWidth,
  objectFit: 'contain',
  position: 'absolute',
  right: 0,
  top: 0,
  width: '100%',
}));

// Component Definition
const Image: FC<HTMLProps<HTMLImageElement> & Props> = ({
  as,
  crossOrigin,
  ref,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleOnLoad = useCallback(() => {
    setIsLoaded(true);
  },
  [setIsLoaded]);

  return (
    <Flex className="image">
      <StyledSpan>
        <StyledImage
          {...props}
          filter={getFilter(props,
            isLoaded)}
          onLoad={handleOnLoad}
        />
        {props.loadingImageSrc && !isLoaded && (
          <AbsoluteImage
            {...props}
            src={props.loadingImageSrc}
          />
        )}
      </StyledSpan>
    </Flex>
  );
};

export default Image;
